import { Component, EventEmitter, inject, Input, Output, ChangeDetectionStrategy } from '@angular/core'
import { MediaPlayerInterface } from '../media-player.interface'
import { SentryService } from '../sentry.service'
import { MediaPlayerControllerComponent } from '../media-player-controller/media-player-controller.component'
import { Call, CallsService, hasUnlabeledSpeakers, MetricType } from '../calls.service'
import { VgApiService, VgCoreModule } from '@videogular/ngx-videogular/core'
import { VgControlsModule } from '@videogular/ngx-videogular/controls'
import { ActivatedRoute, Router } from '@angular/router'
import { DownloadsService } from '../downloads.service'
import { MetricsService } from '../metrics.service'
import { LoggerService } from '../logger.service'
import { UsersService } from '../users.service'

const MEDIA_ERROR_CODE_NAMES: Record<number, string> = {
  1: 'MEDIA_ERR_ABORTED',
  2: 'MEDIA_ERR_NETWORK',
  3: 'MEDIA_ERR_DECODE',
  4: 'MEDIA_ERR_SRC_NOT_SUPPORTED',
}

@Component({
  selector: 'app-video-player',
  standalone: true,
  imports: [MediaPlayerControllerComponent, VgCoreModule, VgControlsModule],
  templateUrl: './video-player.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './video-player.component.scss',
})
export class VideoPlayerComponent implements MediaPlayerInterface {
  @Input()
  call!: Call

  @Output()
  jumpToNextSpeaker = new EventEmitter<number>()

  @Output()
  updateMediaTime = new EventEmitter<number>()

  downloadsService = inject(DownloadsService)
  callsService = inject(CallsService)
  metricsService = inject(MetricsService)
  logger = inject(LoggerService)
  usersService = inject(UsersService)
  sentryService = inject(SentryService)
  mediaApi: VgApiService | null = null
  router = inject(Router)
  route = inject(ActivatedRoute)

  get isPlaying(): boolean {
    if (this.mediaApi === null) {
      return false
    }
    return this.mediaApi.state === 'playing'
  }

  get allowDownload(): boolean {
    return this.call.allowMediaDownload
  }

  get displaySpeakerLabelingModeButton(): boolean {
    return hasUnlabeledSpeakers(this.call)
  }

  get isFullScreen(): boolean {
    if (this.mediaApi === null) {
      return false
    }
    return this.mediaApi.fsAPI.isFullscreen
  }

  togglePlay() {
    if (this.isPlaying) {
      this.pause()
    } else {
      this.play()
    }
  }

  play(): void {
    const videoPlayerElement = this.mediaApi?.getDefaultMedia()?.elem ?? null
    if (videoPlayerElement === null) {
      this.logger.warn('Video player element is not available, cannot play video.')
      return
    }
    videoPlayerElement.play().catch((error: any) => {
      this.logger.error('Error playing video:', error)
    })
  }

  onVideoError(videoElement: HTMLVideoElement): void {
    const mediaError = videoElement.error
    const errorCode = mediaError?.code ?? null
    const errorCodeName = errorCode !== null ? (MEDIA_ERROR_CODE_NAMES[errorCode] ?? String(errorCode)) : 'unknown'
    const errorText = mediaError?.message?.trim() ? mediaError.message : errorCodeName
    const callId = this.call.id
    const currentUser = this.usersService.currentUser$.value
    const userDescription = currentUser ? `${currentUser.email} (${currentUser.id})` : 'unknown'

    const message = `Failed to load the video for call ${callId} for user ${userDescription} because the browser reported a ${errorCodeName} error (code ${errorCode ?? 'unknown'}): ${errorText}.`

    this.logger.error(message)

    const error = new Error(message)
    error.name = 'VideoLoadError'
    this.sentryService.captureException(error, {
      tags: {
        callId: String(callId),
        videoErrorCode: errorCodeName,
      },
      extra: {
        callId,
        errorCode,
        errorCodeName,
        errorText,
        networkState: videoElement.networkState,
        readyState: videoElement.readyState,
      },
    })
  }

  pause(): void {
    if (this.mediaApi === null) {
      this.logger.warn('Media API is not available, cannot pause media.')
      return
    }
    this.mediaApi.pause()
    this.updateMediaTime.emit()
  }

  seek(time: number): void {
    if (this.mediaApi === null) {
      return
    }
    this.mediaApi.seekTime(time)
    this.updateMediaTime.emit()
  }

  changeVolume(volume: number): void {
    if (this.mediaApi === null) {
      return
    }
    this.mediaApi.volume = volume
  }

  getTime(): number {
    const value = this.mediaApi?.currentTime ?? 0
    if (Number.isNaN(value)) {
      return 0
    }
    return value
  }

  getDuration(): number {
    const value = this.mediaApi?.duration ?? 0
    if (Number.isNaN(value)) {
      return 0
    }
    return value
  }

  changePlaybackRate(rate: number): void {
    if (this.mediaApi === null) {
      return
    }
    this.mediaApi.playbackRate = rate
  }

  onPlayerReady(api: VgApiService) {
    this.mediaApi = api
  }

  handleJumpToNextSpeaker() {
    if (this.mediaApi === null) {
      return
    }
    this.jumpToNextSpeaker.emit(this.mediaApi.currentTime)
  }

  toggleFullscreen() {
    if (this.mediaApi === null) {
      return
    }
    this.mediaApi.fsAPI.toggleFullscreen()
  }

  download() {
    this.downloadsService.downloadFromUrl(this.call.videoUrl!)
    this.metricsService.sendMetric(this.call.id, MetricType.DOWNLOAD_VIDEO_RECORDING)
  }
}
